// ✅ src/pages/VehicleV2/TyreMasterReport_AllPrint.jsx
// Prints ONLY the same page data user is currently viewing (page + pageSize passed from listing)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/tyreMasterReport";

const toStr = (v) => String(v ?? "").trim();

function mapFiltersToParams(filters, tyreType, page, pageSize) {
  return {
    tyreType: toStr(tyreType || filters.tyreType || "new"),

    branchType: toStr(filters.branchType || "HO"),
    branch: toStr(filters.branch || "Select"),

    fromDate: toStr(filters.fromDate || ""),
    toDate: toStr(filters.toDate || ""),

    vehicleCode: toStr(filters.vehicleCode || ""),
    vehicleName: toStr(filters.vehicleName || ""),
    regNo: toStr(filters.regNo || filters.vehicleRegNo || ""),

    tyreModel: toStr(filters.tyreModel || "Select"),
    tyreSize: toStr(filters.tyreSize || "Select"),
    tyreSerial: toStr(filters.tyreSerial || "Select"),

    tyreMake: toStr(filters.tyreMake || ""),
    tyreStatus: toStr(filters.tyreStatus || "Select"),

    supplier: toStr(filters.supplier || "Select"),
    vehicleMainType: toStr(filters.vehicleMainType || "Select"),

    // ✅ pagination from listing page
    page: Number(page || 1),
    pageSize: Number(pageSize || 10),
  };
}

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

export default function TyreMasterReportAllPrint() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ passed from listing
  const tyreType = useMemo(() => location?.state?.tyreType || "new", [location]);
  const filters = useMemo(() => location?.state?.filters || {}, [location]);
  const page = useMemo(() => Number(location?.state?.page || 1), [location]);
  const pageSize = useMemo(() => Number(location?.state?.pageSize || 10), [location]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const params = useMemo(
    () => mapFiltersToParams(filters, tyreType, page, pageSize),
    [filters, tyreType, page, pageSize]
  );

  // range text: "Showing 21 to 40 of 263 entries"
  const rangeText = useMemo(() => {
    if (!total) return "";
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `Showing ${start} to ${end} of ${total} entries`;
  }, [page, pageSize, total]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params });
        const apiRows = res?.data?.rows || [];
        const apiTotal = Number(res?.data?.total || 0);

        const normalized = apiRows.map((r, i) => ({
          sno: (page - 1) * pageSize + (i + 1), // ✅ keep original serial position

          vehicleMainType: pick(r, [
            "VehicleType",
            "vehicleMainType",
            "fld_vehiclemaintype",
            "fld_vehiclemaintypename",
            "fld_vehicletypename",
            "vehicletype",
          ]),
          vehicleCode: pick(r, ["VehicleCode", "vehicleCode", "fld_vehicle_code", "fld_vehiclecode"]),
          vehicleName: pick(r, ["VehicleName", "vehicleName", "fld_vehicle_name", "fld_vehiclename"]),
          vehicleRegno: pick(r, ["VehicleRegno", "vehicleRegno", "vehicleRegNo", "regNo", "fld_vehicle_regno"]),

          tyreSize: pick(r, ["TyreSize", "tyreSize", "fld_tyresize"]),
          tyreSerialNo: pick(r, ["TyreSerialNo", "tyreSerialNo", "tyreSerial", "fld_tyreserialno"]),
          tyreModel: pick(r, ["TyreModel", "tyreModel", "fld_tyremodel"]),
          tyreMake: pick(r, ["TyreMake", "tyreMake", "fld_tyremake"]),

          issuedBranch: pick(r, ["IssuedBranch", "issuedBranch", "fld_issuedbranch", "fld_issuebranch", "fld_branchname"]),
          currentBranch: pick(r, ["fld_branchname", "currentBranch", "fld_currentbranch", "fld_carriedbranch"]),

          issuedDate: pick(r, ["IssuedDate", "issuedDate", "issueDate", "fld_issueddate", "fld_issuedate"]),
          supplier: pick(r, ["Supplier", "supplier", "fld_supplier", "fld_purchasefrom", "fld_Retrededfrom"]),
        }));

        if (!alive) return;
        setRows(normalized);
        setTotal(apiTotal);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params, page, pageSize]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Tyre Master Report</h2>
        </div>

        {/* ✅ Range text like listing */}
        <div className="text-xs border border-black px-2 py-1 mb-2">
          <b>Page:</b> {page} &nbsp;&nbsp; <b>Page Size:</b> {pageSize} &nbsp;&nbsp; {rangeText}
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading report...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">VehicleMain Type</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Code</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Regno</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Size</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Serial No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Model</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tyre Make</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Issued Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Current Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Issued Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Supplier</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="border border-black px-2 py-2 text-center">
                    No records found for selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((x, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-black px-1 py-1 text-center">{x.sno}</td>
                    <td className="border border-black px-1 py-1">{x.vehicleMainType}</td>
                    <td className="border border-black px-1 py-1 text-center">{x.vehicleCode}</td>
                    <td className="border border-black px-1 py-1">{x.vehicleName}</td>
                    <td className="border border-black px-1 py-1">{x.vehicleRegno}</td>
                    <td className="border border-black px-1 py-1">{x.tyreSize}</td>
                    <td className="border border-black px-1 py-1">{x.tyreSerialNo}</td>
                    <td className="border border-black px-1 py-1">{x.tyreModel}</td>
                    <td className="border border-black px-1 py-1">{x.tyreMake}</td>
                    <td className="border border-black px-1 py-1">{x.issuedBranch}</td>
                    <td className="border border-black px-1 py-1">{x.currentBranch}</td>
                    <td className="border border-black px-1 py-1 text-center">{x.issuedDate}</td>
                    <td className="border border-black px-1 py-1">{x.supplier}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
